import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Pencil, Loader2, RefreshCw, X } from 'lucide-react';
import {
  MODIFIER_LABELS, READINESS_KEYS, COMPLEXITY_KEYS,
  READINESS_LABELS, READINESS_VALUES, COMPLEXITY_LABELS, COMPLEXITY_VALUES,
} from './modifierConstants';

/**
 * What-If Editor — extracted from ComparePage.js (v221).
 *
 * Behaviour-preserving move. All props, data-testids, and DOM
 * structure match the original inline definition exactly. The
 * 7 modifier-related constants now live in `modifierConstants.js`
 * so the parent page no longer carries them either.
 */
export default function WhatIfEditor({
  whatIfState, setWhatIfState, components, settings,
  recalculating, onRecalculate, onClose, sourceName, baseModifiers,
}) {
  const [activeSection, setActiveSection] = useState('config');

  // Group components by category
  const groups = {};
  components.forEach((c) => {
    if (!groups[c.group]) groups[c.group] = [];
    groups[c.group].push(c);
  });

  const toggleComponent = (name) => {
    setWhatIfState((prev) => {
      const has = prev.selectedNames.includes(name);
      return {
        ...prev,
        selectedNames: has ? prev.selectedNames.filter((n) => n !== name) : [...prev.selectedNames, name],
        quantities: has ? { ...prev.quantities } : { ...prev.quantities, [name]: prev.quantities[name] || 1 },
      };
    });
  };

  const setQuantity = (name, val) => {
    setWhatIfState((prev) => ({ ...prev, quantities: { ...prev.quantities, [name]: Math.max(0.1, parseFloat(val) || 1) } }));
  };

  const setModifier = (key, val) => {
    setWhatIfState((prev) => ({ ...prev, modifiers: { ...prev.modifiers, [key]: val } }));
  };

  const setConfig = (key, val) => {
    setWhatIfState((prev) => ({ ...prev, [key]: val }));
  };

  const SECTIONS = [
    { id: 'config', label: 'Configuration' },
    { id: 'factors', label: 'Project Factors' },
    { id: 'components', label: 'Components' },
  ];

  return (
    <Card className="mb-6 border-amber-300 bg-amber-50/30" data-testid="whatif-editor">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Pencil className="w-4 h-4 text-amber-600" /> What-If Editor
            <Badge className="bg-amber-100 text-amber-700 text-[10px]">Editing clone of: {sourceName}</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#0F2040]" onClick={onRecalculate} disabled={recalculating} data-testid="recalculate-btn">
              {recalculating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Recalculate
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {/* Section tabs */}
        <div className="flex gap-1 bg-white rounded-lg p-0.5 w-fit mb-4">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${activeSection === s.id ? 'bg-[#0F2040] text-white font-medium' : 'text-slate-500 hover:bg-slate-100'}`}
              data-testid={`whatif-tab-${s.id}`}
            >{s.label}</button>
          ))}
        </div>

        {/* Config Section */}
        {activeSection === 'config' && (
          <div className="grid grid-cols-3 gap-4" data-testid="whatif-config">
            <div>
              <Label className="text-xs">Implementation Type</Label>
              <Select value={whatIfState.implementation_type} onValueChange={(v) => setConfig('implementation_type', v)}>
                <SelectTrigger className="mt-1 text-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(settings?.implementation_types || { standard: true, complex: true, enterprise: true }).map((t) => (
                    <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Complexity</Label>
              <Select value={whatIfState.complexity} onValueChange={(v) => setConfig('complexity', v)}>
                <SelectTrigger className="mt-1 text-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['simple', 'standard', 'complex', 'enterprise'].map((c) => (
                    <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Hosting Model</Label>
              <Select value={whatIfState.hosting_model} onValueChange={(v) => setConfig('hosting_model', v)}>
                <SelectTrigger className="mt-1 text-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['public_cloud', 'private_cloud', 'on_premise', 'hybrid'].map((h) => (
                    <SelectItem key={h} value={h}>{h.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Factors Section */}
        {activeSection === 'factors' && (
          <div className="space-y-5" data-testid="whatif-factors">
            <FactorSliderGroup
              dotClass="bg-emerald-500"
              title="Readiness Factors"
              hint="Higher is better"
              keys={READINESS_KEYS}
              labels={READINESS_LABELS}
              values={READINESS_VALUES}
              whatIfState={whatIfState}
              baseModifiers={baseModifiers}
              setModifier={setModifier}
              minLabel="Very Weak"
              maxLabel="Very Strong"
              orientation="readiness"
            />
            <FactorSliderGroup
              dotClass="bg-amber-500"
              title="Complexity & Pressure"
              hint="Lower is better"
              keys={COMPLEXITY_KEYS}
              labels={COMPLEXITY_LABELS}
              values={COMPLEXITY_VALUES}
              whatIfState={whatIfState}
              baseModifiers={baseModifiers}
              setModifier={setModifier}
              minLabel="Low"
              maxLabel="Very High"
              orientation="complexity"
            />
          </div>
        )}

        {/* Components Section */}
        {activeSection === 'components' && (
          <div className="space-y-3 max-h-[350px] overflow-y-auto" data-testid="whatif-components">
            {Object.entries(groups).map(([group, comps]) => (
              <div key={group}>
                <h4 className="text-xs font-semibold text-[#0F2040] mb-1.5 sticky top-0 bg-amber-50/80 py-1">{group}
                  <span className="text-slate-400 font-normal ml-1">({comps.filter((c) => whatIfState.selectedNames.includes(c.name)).length}/{comps.length})</span>
                </h4>
                <div className="grid grid-cols-2 gap-1">
                  {comps.map((c) => {
                    const selected = whatIfState.selectedNames.includes(c.name);
                    return (
                      <div key={c.name} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${selected ? 'bg-white border border-[#0F2040]/20' : 'bg-white/50 border border-transparent'}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleComponent(c.name)}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-[#0F2040]"
                          data-testid={`comp-toggle-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                        />
                        <span className={`flex-1 truncate ${selected ? 'text-[#0F2040] font-medium' : 'text-slate-400'}`} title={c.name}>{c.name}</span>
                        {selected && (
                          <Input
                            type="number"
                            min={0.1}
                            step={0.1}
                            value={whatIfState.quantities[c.name] || 1}
                            onChange={(e) => setQuantity(c.name, e.target.value)}
                            className="w-14 h-6 text-[10px] text-center p-0"
                          />
                        )}
                        <Badge variant="outline" className="text-[9px] shrink-0">{c.role}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Inner sub-component: renders a labeled slider group for a set of
 * factor keys (Readiness or Complexity). Extracted from inline JSX
 * to halve the original 100-line copy-pasted blocks.
 */
function FactorSliderGroup({
  dotClass, title, hint, keys, labels, values,
  whatIfState, baseModifiers, setModifier, minLabel, maxLabel, orientation,
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        <span className="text-xs font-semibold text-[#0F2040] uppercase tracking-wide">{title}</span>
        <span className="text-[10px] text-slate-400">{hint}</span>
      </div>
      <div className="space-y-3">
        {keys.map((key) => {
          const val = whatIfState.modifiers[key] ?? 1.0;
          const baseVal = baseModifiers[key] ?? 1.0;
          let sliderIdx = values.indexOf(val);
          if (sliderIdx === -1) {
            let minDist = Infinity;
            values.forEach((v, i) => { if (Math.abs(v - val) < minDist) { minDist = Math.abs(v - val); sliderIdx = i; } });
          }
          let baseIdx = values.indexOf(baseVal);
          if (baseIdx === -1) {
            let minDist = Infinity;
            values.forEach((v, i) => { if (Math.abs(v - baseVal) < minDist) { minDist = Math.abs(v - baseVal); baseIdx = i; } });
          }
          const labelText = labels[sliderIdx] || (orientation === 'readiness' ? 'Neutral' : 'Average');
          const baseLabelText = labels[baseIdx] || (orientation === 'readiness' ? 'Neutral' : 'Average');
          // Color/direction semantics flip between Readiness (high=good)
          // and Complexity (low=good).
          const labelColor = orientation === 'readiness'
            ? (sliderIdx >= 3 ? 'text-emerald-600' : sliderIdx <= 1 ? 'text-red-500' : 'text-amber-500')
            : (sliderIdx <= 1 ? 'text-emerald-600' : sliderIdx >= 3 ? 'text-red-500' : 'text-amber-500');
          const changed = sliderIdx !== baseIdx;
          const changeDir = sliderIdx - baseIdx;
          const isImproving = orientation === 'readiness' ? changeDir > 0 : changeDir < 0;
          const basePct = (baseIdx / 4) * 100;

          return (
            <div key={key} className={`p-3 rounded-lg border bg-white ${changed ? 'border-amber-400 ring-1 ring-amber-200' : 'border-slate-200'}`} data-testid={`factor-${key}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-[#0F2040]">{MODIFIER_LABELS[key]}</span>
                <div className="flex items-center gap-2">
                  {changed && (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isImproving ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                      {changeDir > 0 ? '↑' : '↓'} from {baseLabelText}
                    </span>
                  )}
                  <span className={`text-xs font-bold ${labelColor}`}>{labelText}</span>
                </div>
              </div>
              <div className="relative">
                <Slider
                  value={[sliderIdx]}
                  min={0}
                  max={4}
                  step={1}
                  onValueChange={([v]) => setModifier(key, values[v])}
                  className="w-full"
                />
                {changed && (
                  <div className="absolute top-0 h-5 pointer-events-none" style={{ left: `${basePct}%`, transform: 'translateX(-50%)' }}>
                    <div className="w-0.5 h-5 bg-blue-400 opacity-70 mx-auto" />
                    <div className="text-[8px] text-blue-500 font-semibold whitespace-nowrap -ml-2 mt-0.5">Base</div>
                  </div>
                )}
              </div>
              <div className="flex justify-between mt-1">
                <span className={`text-[10px] ${orientation === 'readiness' ? 'text-red-400' : 'text-emerald-400'}`}>{minLabel}</span>
                <span className={`text-[10px] ${orientation === 'readiness' ? 'text-emerald-400' : 'text-red-400'}`}>{maxLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
